import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, Form, Input } from "antd";
import Notification from "./components/Notification";
import ResetPassword from "./components/ResetPassword";
import TFAuthentication from "./components/TFAuthentication";
import DownloadApp from "./components/DownloadApp";
import { setUserDetails } from "../../store/Slices/userSlice";
import toast from "react-hot-toast";
import LoadableButton from "../../components/button/LoadableButton";
import ModalComponent from "../../components/modals/ModalComponent";
import Edit from "../../assets/Edit";
import {
  GetSettingsDataApi,
  UpdateProfileApi,
} from "../../api/request/settings/settings";

const Settings = () => {
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.user.userDetails);
  const [isLoading, setIsLoading] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    profile_img: "",
  });
  const [fileList, setFileList] = useState([]);
  const [isFormChanged, setIsFormChanged] = useState(false);

  const handleFieldsChange = (_, allFields) => {
    const isChanged = allFields.some((field) => field.touched);
    setIsFormChanged(isChanged);
  };

  const handleEditClick = () => {
    fileInputRef?.current?.click();
  };

  const handleFileEditChange = (e) => {
    const newFile = e.target.files[0];
    if (newFile) {
      const previewUrl = URL.createObjectURL(newFile);
      const newFileObj = {
        uid: Date.now().toString(),
        name: newFile.name,
        originFileObj: newFile,
        url: previewUrl,
      };
      setFileList([newFileObj]);
      form.setFieldsValue({ profile_img: [newFileObj] });
      setIsFormChanged(true);
    }
  };

  const UploadButton = () => (
    <div className="w-[100px] h-[100px] rounded-lg overflow-hidden flex items-center justify-center">
      {fileList.length > 0 ? (
        <div className="relative h-full w-full">
          <img
            src={fileList[0].url || userDetails?.profile_image}
            alt="avatar"
            className="h-full w-full object-cover"
          />
          <div
            className="absolute bottom-1 right-1 bg-primary rounded-full p-2 cursor-pointer"
            onClick={handleEditClick}
          >
            <Edit color="#fff" className="!size-4" />
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="h-full w-full flex flex-col items-center justify-center gap-2 border border-dashed"
          onClick={handleEditClick}
        >
          <img src="/assets/icons/upload.svg" alt="icon" className="size-5" />
          Upload
        </button>
      )}
    </div>
  );

  const getSettingsData = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetSettingsDataApi();
      if (data?.status) {
        if (data?.data?.profile_image) {
          setFileList([{ uid: "1", url: data?.data?.profile_image }]);
        }
        const profileData = {
          name: data?.data?.name,
          first_name: data?.data?.first_name,
          last_name: data?.data?.last_name,
          profile_image: data?.data?.profile_image,
        };
        dispatch(setUserDetails({ ...userDetails, ...profileData }));
        setInitialValues(profileData);
        setSettingsData(data?.data);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSettingsData();
  }, []);

  const profileUpdate = async (values) => {
    setIsProfileLoading(true);
    try {
      let formData = new FormData();
      formData.append("first_name", values?.first_name);
      formData.append("last_name", values?.last_name);
      if (fileList?.length) {
        formData.append("profile_img", fileList[0]?.originFileObj);
      }
      let apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      const { data } = await UpdateProfileApi(formData, apiHeader);
      if (data?.status) {
        toast.success(data?.message);
        if (data?.data?.profile_image) {
          setFileList([{ uid: "1", url: data?.data?.profile_image }]);
        }
        const updatedData = {
          name: data?.data?.name,
          first_name: data?.data?.first_name,
          last_name: data?.data?.last_name,
          profile_image: data?.data?.profile_image,
        };
        dispatch(setUserDetails({ ...userDetails, ...updatedData }));
        setInitialValues(updatedData);
        setIsModalOpen(false);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsProfileLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen && initialValues) {
      form.setFieldsValue(initialValues);
      setIsFormChanged(false);
    } else {
      form.resetFields();
    }
  }, [isModalOpen, initialValues]);

  return (
    <div className="border bg-White rounded-xl p-4">
      <div className="mb-6">
        <p className="title">Account Details</p>
        <div className="mt-4 flex items-center gap-6">
          <div className="relative">
            <Avatar
              size="large"
              className="uppercase md:size-32 size-24 text-4xl "
              src={userDetails?.profile_image}
            >
              {userDetails?.first_name?.charAt(0)}
              {userDetails?.last_name?.charAt(0)}
            </Avatar>
            <button
              className="bg-primary p-2 rounded-full cursor-pointer absolute bottom-1 right-2"
              onClick={() => setIsModalOpen(true)}
            >
              <Edit color={"#fff"} />
            </button>
          </div>
          <p className="capitalize title">{userDetails?.name}</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        {/* <ProfileAccess /> */}
        <Notification
          isLoading={isLoading}
          settingsData={settingsData}
          setSettingsData={setSettingsData}
        />
        <TFAuthentication
          isLoading={isLoading}
          settingsData={settingsData}
          setSettingsData={setSettingsData}
        />
        <ResetPassword email={userDetails?.email} />
        <DownloadApp />
      </div>

      {/* -------- Profile modal -------- */}
      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title="Update Profile"
        width={600}
      >
        <Form
          form={form}
          autoComplete="off"
          initialValues={initialValues}
          onFinish={profileUpdate}
          onFieldsChange={handleFieldsChange}
        >
          <div>
            <Form.Item name="profile_img">
              <UploadButton />
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileEditChange}
              />
            </Form.Item>
          </div>
          <div>
            <label className="sub-title">First Name</label>
            <Form.Item
              name="first_name"
              className="mb-0"
              rules={[{ required: true, message: "Please enter first name." }]}
            >
              <Input
                placeholder="First name"
                className="mt-1 w-full ps-4 py-2"
              />
            </Form.Item>
          </div>
          <div className="mt-3">
            <label className="sub-title">Last Name</label>
            <Form.Item
              name="last_name"
              className="mb-0"
              rules={[{ required: true, message: "Please enter last name." }]}
            >
              <Input
                placeholder="Last name"
                className="mt-1 w-full ps-4 py-2"
              />
            </Form.Item>
          </div>

          <div className="mt-5">
            <LoadableButton
              type="submit"
              lable="Update"
              disabled={!isFormChanged || isProfileLoading}
              isLoading={isProfileLoading}
              loadingLable="Updating profile..."
            />
          </div>
        </Form>
      </ModalComponent>
    </div>
  );
};

export default Settings;
